<template>
  <div class="location-page">
    <app-bar
      title="Locaties"
      :show-back-button="true"
      :show-profile-button="false"
      :back-button-path="backPath"
    />
    <div v-if="notFound" class="not-found container text-center">
      <p class="mt-8">Deze locatie bestaat niet (meer).</p>
      <router-link
        :to="basePath"
        class="overview-link button button-primary inline-block mt-4 no-underline"
      >
        Bekijk alle locaties
      </router-link>
    </div>
    <div v-if="loadError" class="load-error container text-center">
      <p class="mt-8">Deze locatie kon niet worden geladen.</p>
      <button
        type="button"
        class="retry-button button button-primary inline-block mt-4"
        @click="load"
      >
        Probeer opnieuw
      </button>
    </div>
    <template v-if="selectedLocation">
      <div class="max-w-6xl mx-auto pb-28 lg:px-6 lg:pt-2 lg:pb-16">
        <img
          :src="imageFailed ? noImage : imageUrl"
          alt=""
          class="location-image w-full h-48 lg:h-72 object-cover bg-gray-200 lg:rounded-xl"
          @error="imageFailed = true"
        />
        <div class="lg:flex lg:items-start lg:gap-12 lg:mt-7">
          <div class="flex-1 min-w-0 px-5 lg:px-0">
            <section class="pt-5 lg:pt-0">
              <h1 class="text-2xl lg:text-3xl font-semibold m-0 mb-1">
                {{ selectedLocation.name }}
              </h1>
              <p class="location-subtitle m-0 text-sm text-gray-600">
                {{ subtitle }}
              </p>
              <div
                v-if="lowestNonMemberPrice.amount || hasMemberPrice"
                class="flex flex-wrap items-center gap-2 mt-3"
              >
                <span
                  v-if="lowestNonMemberPrice.amount"
                  class="location-from-price text-sm"
                >
                  v.a.
                  <strong class="font-semibold">
                    {{ $n(lowestNonMemberPrice.amount, 'currency') }}
                  </strong>
                  / {{ lowestNonMemberPrice.duration }} min
                </span>
                <span
                  v-if="hasMemberPrice && lowestMemberPrice.amount"
                  class="location-member-price h-5 leading-5 px-2 rounded-lg bg-green-600 text-white text-xs uppercase"
                >
                  leden: {{ $n(lowestMemberPrice.amount, 'currency') }}
                </span>
              </div>
            </section>

            <location-card-notice
              class="mt-4 lg:hidden"
              v-bind="cardNoticeProps"
            />

            <template v-if="availabilityMessage">
              <div class="alert alert-warning mt-5 text-sm">
                {{ availabilityMessage }}
              </div>
            </template>

            <section
              aria-labelledby="location-book"
              class="mt-6 pt-2 border-t border-gray-200"
            >
              <div class="flex items-baseline justify-between mt-4 mb-2.5">
                <h2 id="location-book" class="text-base font-semibold m-0">
                  Kies een dag
                </h2>
                <span class="text-sm text-gray-500">{{ monthLabel }}</span>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="date-arrow hidden md:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
                  aria-label="Vorige dagen"
                  :disabled="!canScrollBack"
                  @click="scrollDates(-1)"
                >
                  <icon-base size="18"><icon-chevron-left /></icon-base>
                </button>
                <div
                  ref="dateStrip"
                  role="group"
                  aria-label="Kies een dag"
                  class="date-strip relative flex flex-1 min-w-0 gap-2 overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0 pt-0.5 pb-1.5"
                  @scroll="updateScrollState"
                >
                  <button
                    v-for="card in dateCards"
                    :key="card.key"
                    type="button"
                    class="date-card flex-shrink-0 flex flex-col items-center justify-center rounded-lg p-0 cursor-pointer"
                    :class="
                      card.key === booking.date
                        ? 'border-2 border-green-600 bg-green-100 text-green-900'
                        : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-100'
                    "
                    :aria-pressed="card.key === booking.date ? 'true' : 'false'"
                    :aria-label="card.ariaLabel"
                    @click="setParam('date', card.key)"
                  >
                    <span
                      class="text-xs"
                      :class="
                        card.key === booking.date
                          ? 'text-green-800'
                          : 'text-gray-500'
                      "
                    >
                      {{ card.weekday }}
                    </span>
                    <span class="date-card-day text-xl font-semibold">
                      {{ card.day }}
                    </span>
                    <span
                      class="date-card-month"
                      :class="
                        card.key === booking.date
                          ? 'text-green-800'
                          : 'text-gray-500'
                      "
                    >
                      {{ card.month }}
                    </span>
                  </button>
                </div>
                <button
                  type="button"
                  class="date-arrow hidden md:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
                  aria-label="Volgende dagen"
                  :disabled="!canScrollForward"
                  @click="scrollDates(1)"
                >
                  <icon-base size="18"><icon-chevron-right /></icon-base>
                </button>
              </div>
              <p
                v-if="maxPeriodBeforeBooking"
                class="booking-window-hint text-sm text-gray-500 mt-2"
              >
                {{
                  $t('booking.window.hint', { period: maxPeriodBeforeBooking })
                }}
              </p>

              <template
                v-if="
                  ready &&
                  booking.date &&
                  !availabilityLoading &&
                  !dayIsAvailable
                "
              >
                <div
                  v-if="availabilityError"
                  class="availability-error alert alert-warning mt-5 text-sm"
                >
                  De tijden voor deze dag konden niet worden geladen.
                  <button
                    type="button"
                    class="p-0 bg-transparent font-medium underline"
                    @click="fetchAvailability(booking.date)"
                  >
                    Probeer het opnieuw
                  </button>
                </div>
                <div
                  v-else
                  class="no-availability alert alert-warning mt-5 text-sm"
                >
                  Voor deze dag zijn geen tijden beschikbaar
                </div>
              </template>

              <template v-if="booking.date && dayIsAvailable">
                <div>
                  <h2 class="text-base font-semibold mt-6 mb-2.5">
                    Kies een tijd
                  </h2>
                  <div class="flex gap-2 flex-wrap">
                    <button
                      v-for="(obj, index) in availability.availableTimes"
                      :key="index"
                      type="button"
                      class="time-chip border w-16 text-center py-2 rounded-md cursor-pointer hover:bg-gray-100"
                      :class="{
                        'border-2 border-green-600 bg-green-100 text-green-900 hover:bg-green-100':
                          obj.time === booking.time
                      }"
                      :aria-pressed="
                        obj.time === booking.time ? 'true' : 'false'
                      "
                      @click="setParam('time', obj.time)"
                    >
                      <span class="text-sm">{{ obj.time }}</span>
                    </button>
                  </div>
                </div>
                <div v-if="booking.time">
                  <h2 class="text-base font-semibold mt-6 mb-2.5">
                    Kies een duur
                  </h2>
                  <div class="flex gap-2 flex-wrap">
                    <button
                      v-for="(duration, index) in durationOptions"
                      :key="index"
                      type="button"
                      class="duration-chip border px-4 py-2 rounded-md cursor-pointer hover:bg-gray-100"
                      :class="{
                        'border-2 border-green-600 bg-green-100 text-green-900 hover:bg-green-100':
                          duration.key === booking.duration
                      }"
                      :aria-pressed="
                        duration.key === booking.duration ? 'true' : 'false'
                      "
                      @click="setParam('duration', duration.key)"
                    >
                      <span class="text-sm">{{ duration.value }}</span>
                    </button>
                  </div>
                  <p
                    v-if="priceUnavailable"
                    class="price-unavailable mt-2.5 mb-0 text-sm text-red-700"
                  >
                    Deze combinatie is niet beschikbaar, kies een andere duur.
                  </p>
                </div>
              </template>

              <template
                v-if="priceParams.length && booking.date && booking.time"
              >
                <div v-for="(param, index) in priceParams" :key="index">
                  <template
                    v-if="param.type === 'list' && param.key !== 'duration'"
                  >
                    <h2 class="text-base font-semibold mt-6 mb-2.5">
                      {{ param.title }}
                    </h2>
                    <dropdown
                      :items="param.items"
                      :selected="
                        booking[param.key] !== undefined
                          ? booking[param.key]
                          : ''
                      "
                      empty-option="Maak een keuze..."
                      @on-select="(val) => setParam(param.key, val)"
                    />
                  </template>
                  <template v-else-if="param.type === 'boolean'">
                    <div class="flex">
                      <h2 class="flex-1 text-base font-semibold mt-6 mb-2.5">
                        {{ param.title }}
                      </h2>
                      <toggle
                        :value="booking[param.key]"
                        @toggle="(val) => setParam(param.key, val)"
                      />
                    </div>
                    <div v-if="param.description" class="text-xs">
                      {{ param.description }}
                    </div>
                  </template>
                </div>
              </template>
            </section>

            <section
              v-if="descriptionWithBr"
              aria-labelledby="location-about"
              class="mt-8 pt-2 border-t border-gray-200"
            >
              <h2
                id="location-about"
                class="text-base lg:text-lg font-semibold mt-4 mb-2"
              >
                Over deze locatie
              </h2>
              <div
                ref="description"
                class="location-description text-sm lg:text-base leading-relaxed text-gray-600"
                :class="{ 'is-clamped': !descriptionExpanded }"
                v-html="descriptionWithBr"
              />
              <button
                v-if="descriptionClamped && !descriptionExpanded"
                type="button"
                class="read-more md:hidden mt-1.5 p-0 bg-transparent text-sm font-medium text-green-700"
                @click="descriptionExpanded = true"
              >
                Lees meer
              </button>
            </section>

            <div
              class="mt-6 lg:mt-7 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
            >
              <section aria-labelledby="location-address">
                <h2
                  id="location-address"
                  class="text-base lg:text-lg font-semibold m-0 mb-2"
                >
                  Adres
                </h2>
                <p class="m-0 text-sm lg:text-base leading-relaxed">
                  {{ address.street }} {{ address.houseNumber }}
                  <br />
                  {{ address.postalCode }} {{ address.city }}
                </p>
                <a
                  :href="routeUrl"
                  target="_blank"
                  rel="noopener"
                  class="route-link inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-green-700 hover:text-green-800 no-underline"
                >
                  <icon-base size="16" :stroke-width="2">
                    <icon-pin />
                  </icon-base>
                  Route plannen
                </a>
              </section>
              <section
                v-if="Object.keys(timeslots).length"
                aria-labelledby="location-hours"
              >
                <h2
                  id="location-hours"
                  class="text-base lg:text-lg font-semibold m-0 mb-2"
                >
                  Openingstijden
                </h2>
                <timeslots :timeslots="timeslots" />
              </section>
            </div>

            <!-- extra_info without a title has nothing to fold under: show it as is -->
            <div
              v-for="item in extraInfoNotes"
              :key="`note-${item.index}`"
              class="extra-info-note mt-6 text-sm text-gray-600 table"
              v-html="sanitizeHtml(item.content)"
            />

            <section
              v-if="extraInfoSections.length"
              class="mt-6 lg:mt-8 border-t border-gray-200"
            >
              <div
                v-for="{ index, title, content } in extraInfoSections"
                :key="index"
                class="extra-info border-b border-gray-200"
              >
                <button
                  type="button"
                  class="extra-info-toggle w-full flex items-center justify-between p-0 bg-transparent text-left text-gray-800 font-medium"
                  :aria-expanded="openInfo.includes(index) ? 'true' : 'false'"
                  @click="toggleInfo(index)"
                >
                  {{ title }}
                  <icon-base
                    size="18"
                    class="flex-shrink-0 transform transition-transform"
                    :class="
                      openInfo.includes(index) ? '-rotate-90' : 'rotate-90'
                    "
                  >
                    <icon-chevron-right />
                  </icon-base>
                </button>
                <div
                  v-if="openInfo.includes(index)"
                  class="extra-info-content text-sm pb-4 table"
                  v-html="sanitizeHtml(content)"
                />
              </div>
            </section>
          </div>

          <aside
            aria-labelledby="location-summary"
            class="booking-summary hidden lg:flex flex-col gap-4 w-80 flex-shrink-0 sticky top-20 border border-gray-200 rounded-xl p-5"
          >
            <h2 id="location-summary" class="text-lg font-semibold m-0">
              Je boeking
            </h2>
            <dl class="grid grid-cols-2 gap-y-2.5 m-0 text-sm">
              <dt class="text-gray-600 font-normal">Datum</dt>
              <dd class="m-0 text-right">{{ summaryDate }}</dd>
              <dt class="text-gray-600 font-normal">Tijd</dt>
              <dd class="m-0 text-right">{{ summaryTime }}</dd>
              <dt class="text-gray-600 font-normal">Duur</dt>
              <dd class="m-0 text-right">{{ summaryDuration }}</dd>
            </dl>
            <div
              class="flex justify-between items-baseline pt-3.5 border-t border-gray-200"
            >
              <span>Prijs</span>
              <span class="summary-price text-lg font-semibold">
                <template v-if="formValid && showDiscount">
                  <span class="line-through font-normal text-sm mr-1">
                    {{ $n(amountBeforeDiscount, 'currency') }}
                  </span>
                  <span class="text-green-600">{{ priceLabel }}</span>
                </template>
                <template v-else>{{ priceLabel }}</template>
              </span>
            </div>
            <button
              class="book-button button button-primary w-full"
              :disabled="!formValid || isLoading"
              @click="startBooking"
            >
              Boeken
            </button>
            <location-card-notice v-bind="cardNoticeProps" />
          </aside>
        </div>
      </div>

      <div
        class="booking-bar lg:hidden border-t border-gray-300 bottom-0 fixed w-full h-20 bg-white z-10"
      >
        <div class="flex h-full items-center gap-4 px-5 max-w-3xl mx-auto">
          <div class="flex-1 min-w-0 flex flex-col">
            <span class="booking-bar-summary text-xs text-gray-600 truncate">
              {{ barSummary }}
            </span>
            <template v-if="formValid">
              <div v-if="hasValidCard">
                <span class="mr-1.5">Prijs:</span>
                <span class="font-semibold">{{ priceLabel }}</span>
              </div>
              <div
                v-else-if="calculatedPrice || calculatedPrice === 0"
                class="flex items-center"
              >
                <span class="mr-1.5">Prijs:</span>
                <template v-if="showDiscount">
                  <span class="line-through mr-2">
                    {{ $n(amountBeforeDiscount, 'currency') }}
                  </span>
                  <span class="font-semibold text-green-600 mr-2">
                    {{ $n(calculatedPrice, 'currency') }}
                  </span>
                  <button
                    type="button"
                    class="discount-info flex p-0 bg-transparent"
                    aria-label="Uitleg over de korting"
                    @click="showDiscountModal = true"
                  >
                    <icon-base size="24">
                      <icon-info :stroke-width="2" />
                    </icon-base>
                  </button>
                </template>
                <template v-else>
                  <span class="font-semibold">
                    {{ $n(calculatedPrice, 'currency') }}
                  </span>
                </template>
              </div>
            </template>
          </div>
          <button
            class="book-button button button-primary"
            :disabled="!formValid || isLoading"
            @click="startBooking"
          >
            Boeken
          </button>
        </div>
      </div>

      <modal size="lg" :show="showReview" @close="showReview = false">
        <template v-slot:header>Controleer je boeking</template>
        <p class="font-semibold mt-4">Locatie</p>
        <p class="text-sm">{{ selectedLocation.name }}</p>
        <p class="font-semibold mt-4">Datum en tijd</p>
        <p class="text-sm">{{ dateAndTime }}</p>
        <div v-for="(param, index) in priceParams" :key="index">
          <p class="font-semibold mt-4">{{ param.title }}</p>
          <p class="text-sm">
            {{
              typeof booking[param.key] === 'boolean'
                ? booking[param.key]
                  ? 'Ja'
                  : 'Nee'
                : param.key === 'duration'
                ? booking[param.key] + ' minuten'
                : booking[param.key]
            }}
          </p>
        </div>
        <p class="font-semibold mt-4">Prijs</p>
        <template v-if="hasValidCard">
          <p class="text-sm">
            {{ isUnlimitedCard ? 'Seizoenskaart (onbeperkt)' : '1 strip' }}
          </p>
          <p v-if="!isUnlimitedCard" class="text-xs text-gray-500 mt-1">
            Nadat wij je boeking hebben bevestigd per mail wordt er een strip
            van je rittenkaart afgehaald. Je hebt dan nog
            {{ activeCardOnLocation.remaining - 1 }} strip(pen) over.
          </p>
        </template>
        <template v-else>
          <p class="text-sm">{{ $n(calculatedPrice, 'currency') }}</p>
        </template>
        <p class="font-semibold mt-4">Telefoonnummer</p>
        <p class="text-sm">
          Vul je telefoonnummer in zodat we contact kunnen opnemen mocht dat
          nodig zijn
        </p>
        <div class="relative">
          <input-field
            id="phoneNumber"
            v-model="phoneNumber"
            class="mt-2 mb-2"
            default-value="Telefoon nummer"
            @input="storePhoneNumber"
          />
          <i
            v-if="phoneNumber && !phoneNumberError"
            class="absolute top-4 right-4 text-green-600 fal fa-check"
          />
          <div v-if="phoneNumberError" class="text-red-800 mt-3 text-sm">
            Dit is geen correct telefoonnummer
          </div>
        </div>
        <template v-slot:footer>
          <button
            class="button button-primary w-full"
            :disabled="!phoneNumber || phoneNumberError"
            @click="bookLocation"
          >
            Boeken
          </button>
        </template>
      </modal>
      <modal :show="showConfirmation" @close="backToOverview">
        <template v-slot:header>Gelukt!</template>
        <p class="text-center text-4xl mb-6">🎉</p>
        <p class="text-sm">
          We hebben je boeking ontvangen. Je ontvangt zometeen een e-mail met de
          bevestiging van je boeking{{
            hasValidCard ? '.' : ' en een link om te betalen.'
          }}
          Heb je niks ontvangen? Check dan ook even je spamfolder of neem
          contact op via contact@inviplay.nl
        </p>
        <p v-if="paymentDeadlineMessage" class="text-sm font-bold mt-4">
          {{ paymentDeadlineMessage }}
        </p>
      </modal>
      <modal :show="showError" @close="showError = false">
        <template v-slot:header>Oeps!</template>
        <p class="text-center text-4xl mb-6">👀</p>
        <p v-if="errorMessage" class="text-sm">{{ errorMessage }}</p>
        <p v-else class="text-sm">
          Er is iets fout gegaan. Probeer het nog een keer of neem contact op
          via contact@inviplay.nl
        </p>
      </modal>
      <modal
        v-if="discount && discount.value && discount.type"
        :show="showDiscountModal"
        @close="showDiscountModal = false"
      >
        <template v-slot:header>Korting!</template>
        <p class="text-center text-4xl mb-6">🏆</p>
        <p class="text-sm">
          Voor deze boeking krijg je een korting van
          <strong>
            {{
              discount.type === 'percentage'
                ? `${discount.value}%`
                : $n(discount.value / 100, 'currency')
            }}
          </strong>
          .
        </p>
      </modal>
      <modal
        v-if="!isLoading"
        :show="showFeedbackModal"
        :show-close-icon="true"
        @close="closeFeedbackModal"
      >
        <template v-slot:header>
          {{ paymentIsSuccess ? 'Gelukt' : 'Oeps' }}
        </template>
        <template v-if="action === 'payment'">
          <p v-if="paymentIsSuccess">
            Je betaling is ontvangen en je kunt je strippenkaart nu gebruiken om
            te boeken
          </p>
          <p v-else>Er is iets fout gegaan</p>
        </template>
      </modal>
    </template>
    <!-- last: a fixed overlay that, placed before the content, makes Vue
         detach and reinsert the content, which resets the date strip's scroll -->
    <spinner-loader v-if="isLoading" />
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import SpinnerLoader from '@/components/SpinnerLoader'
import {
  LOCATION_BOOK_COMMERCIAL,
  LOCATION_GET_COMMERCIAL_AVAILABILITY,
  LOCATION_GET_COMMERCIAL_BY_SLUG
} from '../../store/actions/location'
import { ACTIVITY_LIST } from '@/store/actions/activity'
import Dropdown from '@/components/Dropdown'
import InputField from '@/components/InputField'
import IconBase from '@/components/IconBase'
import IconChevronLeft from '@/components/icons/IconChevronLeft'
import IconChevronRight from '@/components/icons/IconChevronRight'
import IconInfo from '@/components/icons/IconInfo'
import IconPin from '@/components/icons/IconPin'
import Toggle from '../../components/Toggle.vue'
import Timeslots from './Timeslots.vue'
import LocationCardNotice from './LocationCardNotice.vue'
import { DateTime, Interval } from 'luxon'
import Modal from '@/components/Modal'
import {
  PARTICIPANT_OWN,
  PARTICIPANT_UPDATE_PHONENUMBER
} from '../../store/actions/participant'
import { mapGetters, mapState } from 'vuex'
import { PAYMENT_GET_STATUS_BY_ID } from '../../store/actions/payment'
import { CREDITCARD_GET_ALL_OF_USER_ON_LOCATION } from '../../store/actions/creditcard'
import api from '@/utils/api'
import config from '@/utils/config'
import analytics from '@/utils/analytics'
import { sanitizeHtml } from '@/utils/sanitize'
import { OG_DEFAULT_IMAGE, truncate } from '@/utils/seo'
import { PRICING_PARAM_MEMBER } from '@/utils/constants'
import noImage from '@/assets/images/img_no-image.png'
import {
  bookingWindowDays,
  buildDateCards,
  describePaymentDeadline,
  durationLabel,
  formatBookingDate,
  formatBookingMonth,
  formatSlots,
  formatTimeRange,
  lowestPrice
} from '@/utils/booking'

// the query keys that hold the booker's choice (?date=…&time=…&duration=…);
// the list and boolean pricing params of a location come on top of these
const CHOICE_KEYS = ['date', 'time', 'duration']
const isChoiceParam = (param) =>
  !!param &&
  !CHOICE_KEYS.includes(param.key) &&
  (param.type === 'list' || param.type === 'boolean')

export default {
  name: 'LocationPage',
  components: {
    AppBar,
    SpinnerLoader,
    IconBase,
    IconChevronLeft,
    IconChevronRight,
    IconInfo,
    IconPin,
    Dropdown,
    Toggle,
    Modal,
    InputField,
    Timeslots,
    LocationCardNotice
  },
  data() {
    return {
      selectedLocation: null,
      notFound: false,
      // the location could not be loaded (API error), which is not the same
      // as an unknown slug: no noindex, and a retry
      loadError: false,
      ready: false,
      booking: {},
      // the times of booking.date at this location; only the answer to the
      // latest request lands here (see fetchAvailability)
      availability: null,
      availabilityLoading: false,
      availabilityError: false,
      availabilityRequest: 0,
      maxHoursBeforeBooking: undefined,
      calculatedPrice: 0,
      amountBeforeDiscount: 0,
      showReview: false,
      showConfirmation: false,
      phoneNumberError: false,
      phoneNumber: undefined,
      showError: false,
      errorMessage: '',
      paymentId: this.$route.query.pid,
      showFeedbackModal: false,
      discount: undefined,
      showDiscountModal: false,
      priceLoading: false,
      // the price call found no price for this combination of choices
      priceUnavailable: false,
      createdBooking: null,
      canScrollBack: false,
      canScrollForward: true,
      descriptionExpanded: false,
      descriptionClamped: false,
      openInfo: [],
      imageFailed: false,
      noImage
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
    ...mapState({
      profile: (state) => state.participant.ownProfile,
      paymentStatus: (state) => state.payment.status,
      storedCard: (state) => state.creditcard.activeCardOnLocation,
      activities: (state) => state.activity?.activityList || [],
      stateLoading: (state) =>
        state.participant.status === 'loading' ||
        state.payment.loading ||
        state.creditcard.loading ||
        state.location.status === 'loading'
    }),
    // the page is public: a card left in the store by an earlier session
    // (logout does not clear it) is not the card of a guest
    activeCardOnLocation() {
      return this.isAuthenticated ? this.storedCard : null
    },
    locationSlug() {
      return this.$route.params.location
    },
    // '/locaties' or '/locations', whichever the visitor came in on
    basePath() {
      const path = this.$route.path || '/locaties'
      const end = path.indexOf('/', 1)
      return end > 0 ? path.substring(0, end) : path
    },
    pagePath() {
      return `${this.basePath}/${this.locationSlug}`
    },
    backPath() {
      return this.$route.query.c
        ? `${this.basePath}?c=${this.$route.query.c}`
        : this.basePath
    },
    imageUrl() {
      const url = this.selectedLocation?.imageUrl
      if (!url) return noImage
      return url.startsWith('http') ? url : `${config.baseUrl}${url}`
    },
    address() {
      return this.selectedLocation?.address || {}
    },
    sportName() {
      return (this.selectedLocation?.activities || [])
        .map((id) => this.activities.find((a) => a.id === id)?.name)
        .filter(Boolean)
        .join(', ')
    },
    subtitle() {
      return [this.address.city, this.sportName].filter(Boolean).join(' · ')
    },
    hasMemberPrice() {
      return !!this.selectedLocation?.pricing?.params?.find(
        (param) => param.key === PRICING_PARAM_MEMBER
      )
    },
    lowestMemberPrice() {
      return lowestPrice(this.selectedLocation?.pricing, true)
    },
    lowestNonMemberPrice() {
      return lowestPrice(this.selectedLocation?.pricing, false)
    },
    timeslots() {
      return this.selectedLocation?.timeslots
        ? formatSlots(this.selectedLocation.timeslots)
        : {}
    },
    extraInfo() {
      return Array.isArray(this.selectedLocation?.extra_info)
        ? this.selectedLocation.extra_info.map((item, index) => ({
            ...item,
            index
          }))
        : []
    },
    extraInfoSections() {
      return this.extraInfo.filter((item) => item.title?.trim())
    },
    extraInfoNotes() {
      return this.extraInfo.filter(
        (item) => !item.title?.trim() && item.content?.trim()
      )
    },
    descriptionWithBr() {
      const description = this.selectedLocation?.description?.replace(
        /(?:\r\n|\r|\n)/g,
        '<br />'
      )
      return description ? sanitizeHtml(description) : undefined
    },
    routeUrl() {
      const { lat, lng } = this.selectedLocation?.coordinates || {}
      const destination =
        lat && lng
          ? `${lat},${lng}`
          : [
              `${this.address.street || ''} ${this.address.houseNumber || ''}`,
              this.address.postalCode,
              this.address.city
            ]
              .map((part) => String(part || '').trim())
              .filter(Boolean)
              .join(', ')
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        destination
      )}`
    },
    cardNoticeProps() {
      return {
        hasValidCard: !!this.hasValidCard,
        isUnlimitedCard: this.isUnlimitedCard,
        remaining: this.activeCardOnLocation?.remaining || 0,
        creditcards: this.selectedLocation?.creditcards || [],
        origin: this.pagePath
      }
    },
    dateCards() {
      return buildDateCards(bookingWindowDays(this.maxHoursBeforeBooking))
    },
    monthLabel() {
      return formatBookingMonth(this.booking.date)
    },
    barSummary() {
      if (!this.booking.date) return 'Kies een dag, tijd en duur'
      const date = formatBookingDate(this.booking.date)
      if (!this.booking.time) return `${date} · kies een tijd`
      return `${date} · ${formatTimeRange(
        this.booking.time,
        this.booking.duration
      )}`
    },
    summaryDate() {
      return this.booking.date
        ? formatBookingDate(this.booking.date, true)
        : '—'
    },
    summaryTime() {
      return this.booking.time
        ? formatTimeRange(this.booking.time, this.booking.duration, ' – ')
        : '—'
    },
    summaryDuration() {
      return this.booking.duration ? durationLabel(this.booking.duration) : '—'
    },
    showDiscount() {
      return !!(
        !this.hasValidCard &&
        this.discount &&
        this.discount.value &&
        this.amountBeforeDiscount
      )
    },
    priceLabel() {
      if (!this.formValid) return '—'
      if (this.hasValidCard)
        return this.isUnlimitedCard ? 'seizoenskaart' : '1 strip'
      return this.calculatedPrice || this.calculatedPrice === 0
        ? this.$n(this.calculatedPrice, 'currency')
        : '—'
    },
    isUnlimitedCard: function () {
      return (
        this.activeCardOnLocation?.type === 'season_card' ||
        this.activeCardOnLocation?.remaining === null
      )
    },
    hasValidCard: function () {
      return (
        this.activeCardOnLocation &&
        (this.isUnlimitedCard || this.activeCardOnLocation?.remaining > 0)
      )
    },
    isLoading: function () {
      return this.stateLoading || this.priceLoading
    },
    action: function () {
      return this.$route.query.action
    },
    dateAndTime: function () {
      const d = DateTime.fromISO(this.booking?.date).toLocaleString(
        DateTime.DATE_FULL
      )
      const t = DateTime.fromISO(this.booking?.time)
      return `${d} van ${t.toFormat('T')} - ${t
        .plus({
          minutes: this.booking?.duration
        })
        .toFormat('T')}`
    },
    durationOptions: function () {
      return this.booking.time
        ? (
            this.availability?.availableTimes?.find(
              (obj) => obj.time === this.booking.time
            )?.durationOptions || []
          ).map((duration) => ({
            key: duration,
            value: durationLabel(duration)
          }))
        : []
    },
    priceParams: function () {
      return (
        (this.booking.date &&
          this.booking.time &&
          this.selectedLocation?.pricing?.params?.reduce(
            (params, item) => [
              ...params,
              ...(item.type !== 'calculated'
                ? [
                    {
                      ...item,
                      items: this.selectedLocation?.pricing?.data?.reduce(
                        (pricingData, d) => [
                          ...pricingData,
                          ...(pricingData.find(
                            (pd) => pd.key === d[item.key]
                          ) ||
                          (d.time_frame &&
                            !this.isInTimeFrame(
                              this.booking.date,
                              this.booking.time,
                              d.time_frame
                            ))
                            ? []
                            : [{ key: d[item.key], value: d[item.key] }])
                        ],
                        []
                      )
                    }
                  ]
                : [...(params.value === item.value ? [] : [{ ...item }])])
            ],
            []
          )) ||
        []
      )
    },
    paymentIsSuccess: function () {
      return this.paymentId && this.paymentStatus
        ? this.paymentStatus[this.paymentId] === 'paid' ||
            this.paymentStatus[this.paymentId] === 'completed'
        : false
    },
    formValid: function () {
      return (
        ((this.calculatedPrice ||
          this.calculatedPrice === 0 ||
          this.hasValidCard) &&
          !!this.booking?.date &&
          !!this.booking?.time &&
          !!this.booking?.duration) ||
        false
      )
    },
    maxPeriodBeforeBooking: function () {
      return this.maxHoursBeforeBooking
        ? this.maxHoursBeforeBooking > 48
          ? this.maxHoursBeforeBooking / 24 + ' dagen'
          : `${this.maxHoursBeforeBooking} uur`
        : undefined
    },
    dayIsAvailable: function () {
      return !!this.availability?.availableTimes?.length
    },
    availabilityMessage: function () {
      return this.selectedLocation?.settings?.availability_message
    },
    paymentDeadlineMessage: function () {
      const deadline = describePaymentDeadline(
        this.createdBooking?.paymentDeadline,
        new Date(),
        this.$i18n?.locale
      )
      if (!deadline) return ''
      return this.$t(`booking.paymentDeadline.${deadline.when}`, {
        time: deadline.time,
        date: deadline.date
      })
    },
    // every query key of the choice at this location
    choiceKeys() {
      return [
        ...CHOICE_KEYS,
        ...(this.selectedLocation?.pricing?.params || [])
          .filter(isChoiceParam)
          .map((param) => param.key)
      ]
    },
    choiceKey() {
      return this.choiceKeys.map((key) => this.booking[key] || '').join('|')
    }
  },
  metaInfo() {
    const loc = this.selectedLocation
    if (loc && loc.name) return this.locationMetaInfo(loc)
    if (this.notFound) {
      return {
        title: 'Locatie niet gevonden | Inviplay',
        meta: [{ vmid: 'robots', name: 'robots', content: 'noindex' }]
      }
    }
    return { title: 'Locatie boeken | Inviplay' }
  },
  watch: {
    action(action) {
      this.showFeedbackModal = action ? true : false
    },
    booking(params) {
      if (params.date && params.time && params.duration) {
        this.getPrice(params)
      } else {
        this.priceUnavailable = false
      }
    },
    'booking.time'() {
      if (
        this.booking.duration &&
        !this.durationOptions
          .map((item) => item.key)
          .includes(this.booking.duration)
      ) {
        this.booking.duration = null
      }
    },
    choiceKey() {
      this.syncQuery()
    },
    profile(p) {
      if (p && p.phoneNumber && !this.phoneNumber) {
        this.phoneNumber = p.phoneNumber
      }
    },
    descriptionWithBr() {
      this.$nextTick(this.measureDescription)
    },
    locationSlug(slug, previous) {
      if (slug && slug !== previous) this.load()
    }
  },
  created: function () {
    if (!this.activities.length) {
      this.$store.dispatch(ACTIVITY_LIST)
    }
    if (this.isAuthenticated && !this.phoneNumber) {
      this.$store.dispatch(PARTICIPANT_OWN)
    }
    if (this.action) {
      this.showFeedbackModal = true
      if (this.action === 'payment' && this.paymentId) {
        // get payment status of this user
        this.$store.dispatch(PAYMENT_GET_STATUS_BY_ID, {
          paymentId: this.paymentId
        })
      }
    }
    this.load()
  },
  methods: {
    sanitizeHtml,
    async load() {
      // read the choice before anything rewrites the query
      const initial = { ...this.$route.query }
      this.ready = false
      this.notFound = false
      this.loadError = false
      this.booking = {}
      // drop the times of the previous location, and any answer still on its way
      this.availabilityRequest++
      this.availability = null
      this.openInfo = []
      this.descriptionExpanded = false
      this.imageFailed = false
      const location = this.locationSlug
        ? await this.$store.dispatch(
            LOCATION_GET_COMMERCIAL_BY_SLUG,
            this.locationSlug
          )
        : null
      if (!location) {
        this.selectedLocation = null
        // the action returns nothing for an unknown slug and for an error;
        // only the store status tells them apart
        if (this.$store.state.location?.status === 'error') {
          this.loadError = true
        } else {
          this.notFound = true
        }
        return
      }
      this.selectedLocation = location
      this.maxHoursBeforeBooking =
        location.settings?.max_hours_before_booking ?? 0
      analytics.capture('location_viewed', { location_id: location.id })
      if (this.isAuthenticated) {
        this.$store.dispatch(CREDITCARD_GET_ALL_OF_USER_ON_LOCATION, {
          locationId: location.id
        })
      }
      await this.restoreChoice(initial)
      this.ready = true
      this.syncQuery()
      this.$nextTick(() => {
        this.scrollSelectedDateIntoView()
        this.updateScrollState()
        this.measureDescription()
      })
    },
    // Puts the choice from the query (?date=…&time=…&duration=…) back, as
    // far as it is still bookable. Without a (valid) date: today.
    async restoreChoice(query) {
      const date = this.dateCards.find((card) => card.key === query.date)
        ? query.date
        : this.dateCards[0]?.key
      if (!date) return
      this.booking = { date }
      await this.fetchAvailability(date)
      const slot = this.availability?.availableTimes?.find(
        (obj) => obj.time === query.time
      )
      if (!slot) return
      const duration = parseInt(query.duration)
      this.booking = {
        date,
        time: slot.time,
        ...((slot.durationOptions || []).includes(duration) ? { duration } : {})
      }
      // the other pricing params, as far as the value is still an option
      // (priceParams needs the date and time set above)
      const params = {}
      this.priceParams.filter(isChoiceParam).forEach((param) => {
        const value = query[param.key]
        // false is never written to the query: absent means off, as on a
        // fresh choice, so the price call always gets the param
        if (param.type === 'boolean') {
          params[param.key] = value === 'true'
          return
        }
        if (value === undefined) return
        const item = (param.items || []).find(
          (option) => String(option.key) === String(value)
        )
        if (item) params[param.key] = item.key
      })
      if (Object.keys(params).length) {
        this.booking = { ...this.booking, ...params }
      }
    },
    syncQuery() {
      if (!this.ready) return
      const current = this.$route.query || {}
      const query = { ...current }
      // today without a time is the default: keep the URL clean for it
      const isDefault =
        !this.booking.time && this.booking.date === this.dateCards[0]?.key
      this.choiceKeys.forEach((key) => {
        delete query[key]
        const value = this.booking[key]
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          value !== false &&
          !isDefault
        ) {
          query[key] = String(value)
        }
      })
      const same =
        Object.keys(query).length === Object.keys(current).length &&
        Object.keys(query).every((key) => String(current[key]) === query[key])
      if (same) return
      const result = this.$router.replace({ path: this.$route.path, query })
      if (result && result.catch) result.catch(() => {})
    },
    // Loads the times of one day. The previous day's times go away at once,
    // and an answer that arrives after a newer request (another day or
    // location) is dropped, so the chips never show times of another day.
    async fetchAvailability(date) {
      const request = ++this.availabilityRequest
      const locationId = this.selectedLocation.id
      this.availability = null
      this.availabilityError = false
      this.availabilityLoading = true
      const result = await this.$store.dispatch(
        LOCATION_GET_COMMERCIAL_AVAILABILITY,
        { locationId, date: DateTime.fromISO(date).toISODate() }
      )
      if (request !== this.availabilityRequest) return
      this.availability = result || null
      this.availabilityError = !result
      this.availabilityLoading = false
    },
    // Boeken: open the review, or first the login flow. The choice is in the
    // query, so coming back from the login restores it.
    startBooking() {
      if (!this.formValid) return
      if (!this.isAuthenticated) {
        this.$router.push({
          path: '/login',
          query: { redirectTo: this.$route.fullPath }
        })
        return
      }
      this.showReview = true
    },
    closeFeedbackModal() {
      const query = { ...this.$route.query }
      delete query.action
      delete query.pid
      this.showFeedbackModal = false
      this.$router.push({ path: this.$route.path, query })
    },
    scrollDates(direction) {
      const strip = this.$refs.dateStrip
      if (!strip || !strip.scrollBy) return
      strip.scrollBy({
        left: direction * strip.clientWidth,
        behavior: 'smooth'
      })
    },
    updateScrollState() {
      const strip = this.$refs.dateStrip
      if (!strip) return
      this.canScrollBack = strip.scrollLeft > 0
      this.canScrollForward =
        strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1
    },
    scrollSelectedDateIntoView() {
      const strip = this.$refs.dateStrip
      const selected = strip?.querySelector('[aria-pressed="true"]')
      if (!selected || !strip.clientWidth) return
      const left =
        selected.offsetLeft - strip.clientWidth / 2 + selected.offsetWidth / 2
      strip.scrollLeft = Math.max(0, left)
    },
    measureDescription() {
      const el = this.$refs.description
      this.descriptionClamped = !!el && el.scrollHeight > el.clientHeight + 1
    },
    toggleInfo(index) {
      this.openInfo = this.openInfo.includes(index)
        ? this.openInfo.filter((i) => i !== index)
        : [...this.openInfo, index]
    },
    locationMetaInfo(loc) {
      const address = loc.address || {}
      const city = address.city || ''
      const title = `${loc.name}${city ? ` – ${city}` : ''} | Inviplay`
      const description = truncate(
        loc.description ||
          `Boek ${loc.name}${
            city ? ` in ${city}` : ''
          } via Inviplay. Bekijk beschikbare tijden en reserveer direct.`,
        160
      )
      const image =
        this.imageUrl && this.imageUrl.startsWith('http')
          ? this.imageUrl
          : OG_DEFAULT_IMAGE
      // one canonical address for both /locaties/… and the /locations/… alias
      const url = `${window.location.origin}/locaties/${loc.slug}`

      const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SportsActivityLocation',
        name: loc.name,
        description,
        image,
        url,
        address: {
          '@type': 'PostalAddress',
          streetAddress: [address.street, address.houseNumber]
            .filter(Boolean)
            .join(' '),
          postalCode: address.postalCode,
          addressLocality: city,
          addressCountry: 'NL'
        }
      }
      if (loc.coordinates && loc.coordinates.lat && loc.coordinates.lng) {
        jsonLd.geo = {
          '@type': 'GeoCoordinates',
          latitude: loc.coordinates.lat,
          longitude: loc.coordinates.lng
        }
      }

      return {
        title,
        meta: [
          { vmid: 'description', name: 'description', content: description },
          { vmid: 'og:title', property: 'og:title', content: title },
          {
            vmid: 'og:description',
            property: 'og:description',
            content: description
          },
          { vmid: 'og:image', property: 'og:image', content: image },
          { vmid: 'og:url', property: 'og:url', content: url },
          {
            vmid: 'og:type',
            property: 'og:type',
            content: 'business.business'
          },
          { vmid: 'twitter:title', name: 'twitter:title', content: title },
          {
            vmid: 'twitter:description',
            name: 'twitter:description',
            content: description
          },
          { vmid: 'twitter:image', name: 'twitter:image', content: image }
        ],
        link: [{ vmid: 'canonical', rel: 'canonical', href: url }],
        script: [
          { vmid: 'ld-location', type: 'application/ld+json', json: jsonLd }
        ]
      }
    },
    async getPrice(params) {
      const dateTime = DateTime.fromISO(params.date).set({
        hour: params.time.split(':')[0],
        minute: params.time.split(':')[1]
      })
      this.priceLoading = true
      this.priceUnavailable = false
      try {
        const resp = await api.post(
          `/commercial_location/${this.selectedLocation.id}/price`,
          {
            params: { dateTime, ...params }
          }
        )
        const amount = resp.data?.amount
        if (resp.status === 200 && typeof amount === 'number') {
          this.discount = resp.data.discount
          this.amountBeforeDiscount = resp.data.amountBeforeDiscount / 100
          this.calculatedPrice = amount / 100
        } else if (resp.status === 200) {
          // e.g. { message: 'The combination of chosen options is not available' }
          this.discount = undefined
          this.calculatedPrice = undefined
          this.priceUnavailable = true
        }
      } catch (e) {
        this.calculatedPrice = undefined
      }
      this.priceLoading = false
    },
    isInTimeFrame(date, time, timeFrame) {
      const d = DateTime.fromISO(date)
      const t = DateTime.fromISO(time)
      const dateTime = d.set({ hour: t.hour, minute: t.minute })
      const startDateTime = DateTime.fromFormat(timeFrame.start, 'TT').set({
        day: d.day,
        month: d.month,
        year: d.year
      })
      const endDateTime = DateTime.fromFormat(timeFrame.end, 'TT').set({
        day: d.day,
        month: d.month,
        year: d.year
      })
      const interval = Interval.fromDateTimes(startDateTime, endDateTime)
      return interval.contains(dateTime)
    },
    async bookLocation() {
      this.showReview = false
      const d = DateTime.fromISO(this.booking.date)
      const t = DateTime.fromISO(this.booking.time)
      const startDateTime = d.set({ hour: t.hour, minute: t.minute })
      const endDateTime = startDateTime.plus({ minutes: this.booking.duration })
      const bookingObj = {
        startDateTime: startDateTime.toISO(),
        endDateTime: endDateTime.toISO(),
        commercial_location_id: this.selectedLocation.id,
        booking_params: this.priceParams.reduce(
          (res, p) => ({
            ...res,
            [p.key]: this.booking[p.key]
          }),
          { duration: this.booking.duration }
        ),
        use_creditcard: this.hasValidCard
      }
      this.errorMessage = ''
      let resp
      try {
        resp = await this.$store.dispatch(LOCATION_BOOK_COMMERCIAL, bookingObj)
      } catch (error) {
        if (error?.code === 'BOOKING_TOO_FAR_AHEAD') {
          this.errorMessage =
            error.message ||
            this.$t('booking.window.tooFarAhead', {
              period: this.maxPeriodBeforeBooking
            })
        }
      }
      if (resp) {
        this.createdBooking = resp
        this.showConfirmation = true
      } else {
        this.showError = true
      }
    },
    backToOverview() {
      this.$router.push(this.basePath)
    },
    setParam(type, val) {
      if (type === 'date') {
        if (val === this.booking.date) return
        this.booking = {
          date: val
        }
        this.fetchAvailability(val)
      } else {
        this.booking = {
          ...this.booking,
          [type]: val
        }
      }
    },
    isValid() {
      const regex = RegExp(
        /^((\+|00(\s|\s?-\s?)?)31(\s|\s?-\s?)?(\(0\)[-\s]?)?|0)[1-9]((\s|\s?-\s?)?[0-9])((\s|\s?-\s?)?[0-9])((\s|\s?-\s?)?[0-9])\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]$/
      )
      return regex.test(this.phoneNumber)
    },
    storePhoneNumber() {
      setTimeout(() => {
        if (this.isValid()) {
          this.phoneNumberError = false
          this.$store.dispatch(PARTICIPANT_UPDATE_PHONENUMBER, {
            phoneNumber: this.phoneNumber
          })
        } else {
          this.phoneNumberError = true
        }
      }, 500)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.date-strip {
  scrollbar-width: none;
  scroll-snap-type: x proximity;
  scroll-padding-left: 20px;
  @media (min-width: 768px) {
    scroll-padding-left: 0;
  }
  &::-webkit-scrollbar {
    display: none;
  }
}

.date-card {
  width: 56px;
  height: 76px;
  gap: 1px;
  scroll-snap-align: start;
}

.date-card-day {
  line-height: 1.1;
}

.date-card-month {
  font-size: 11px;
  line-height: 15px;
  min-height: 15px;
}

.extra-info-toggle {
  height: 52px;
}

// mobile: the description is cut off after three lines behind "Lees meer"
@media (max-width: 767px) {
  .location-description.is-clamped {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.table {
  ::v-deep table {
    padding: 0.5rem;
    font-size: 12px !important;
  }
}
</style>
